// ══════════════════════════════════════════════════════════════════════════
//  Nombres de identidad en MAYÚSCULAS (2026-09-14, decisión del dueño).
//
//  Obras, clientes y proyectos (y a quién va dirigido un presupuesto) se guardan
//  canónicos: mayúsculas y sin espacios en los bordes. El documento (PDF y vista
//  de lectura, documento.logic.lineasDestinatario) siempre los pintó así, pero
//  la captura guardaba lo que viniera y las listas, las carpetas, el buscador,
//  los correos y el Inicio mostraban "amco" junto a "AMCO", "Chileno Bay " con
//  espacio final e "ing. pablo quintana".
//
//  La base lo sostiene con los triggers trg_obras_mayusculas,
//  trg_clientes_mayusculas y trg_presupuestos_mayusculas (migración
//  nombres_identidad_en_mayusculas): cualquier vía (app, MCP, consola) guarda
//  canónico. Aquí vive el ESPEJO para que el estado local de la app coincida
//  con lo guardado sin esperar una recarga: los callbacks de escritura del
//  shell y las pantallas que escriben directo pasan la fila por `enMayusculas`.
//  Puro, con vitest.
// ══════════════════════════════════════════════════════════════════════════

import { plegar } from './busqueda.logic.js';

// Espejo exacto de las columnas que tocan los triggers.
export const CAMPOS_MAYUSCULAS = Object.freeze({
  obras:        ['nombre', 'cliente'],
  clientes:     ['alias', 'razon_social'],
  presupuestos: ['nombre', 'proyecto_nombre', 'empresa_nombre', 'destinatario'],
});

// upper(btrim(x)) de Postgres, en el navegador: lo que no es texto (null,
// undefined, números) pasa intacto.
export const mayusculas = (v) => (typeof v === 'string' ? v.trim().toUpperCase() : v);

// Lo que se está TECLEANDO, ya canónico: mayúsculas SIN recortar los bordes,
// porque `trim` en cada pulsación impide escribir el espacio entre dos palabras.
// El nombre queda en mayúsculas desde la captura, así que lo que la pantalla
// deriva de él en ese momento (el enlace "Registrar X como cliente", un aviso,
// el título automático, la vista previa) dice lo mismo que se guardará; el
// recorte de los bordes lo hace `mayusculas` al escribir.
export const mayusculasVivas = (v) => (typeof v === 'string' ? v.toUpperCase() : v);

// ¿Ese campo de esa tabla es un nombre de identidad? Para los formularios con un
// setter genérico `set(campo, valor)`, que así canonizan sin listar los campos otra vez.
export const esNombreDeIdentidad = (tabla, campo) => (CAMPOS_MAYUSCULAS[tabla] || []).includes(campo);

// Una fila o un parche con sus campos de identidad canónicos. Solo toca los
// campos que vienen: un parche de `monto_total` sale igual que entró.
export function enMayusculas(tabla, fila) {
  const campos = CAMPOS_MAYUSCULAS[tabla];
  if (!campos || !fila || typeof fila !== 'object') return fila;
  const out = { ...fila };
  for (const k of campos) if (k in out) out[k] = mayusculas(out[k]);
  return out;
}

// ── Las siglas de un nombre (2026-10-17, nacidas en las entregas de calidad) ─
// Nombran lo generado cuando el nombre entero no cabe: la clave de una entrega
// (calidad/entrega.logic.js) y, desde el 2026-10-22, el cliente en el archivo
// del presupuesto (presupuestos/archivo.logic.js).

// Las palabras que no dicen nada de un nombre: no dan letra a sus siglas.
const PALABRAS_VACIAS = new Set(['DE', 'DEL', 'LA', 'LAS', 'LOS', 'EL', 'Y', 'E', 'EN', 'PARA', 'POR', 'CON', 'A', 'AL', 'O', 'U', 'NO', 'SA', 'CV', 'SC', 'SAPI']);
/** Lo más que miden las siglas de un nombre. */
export const LARGO_SIGLAS = 8;

/**
 * Las siglas de un nombre: la inicial de cada palabra que dice algo (un número va entero), sin acentos; una sola palabra
 * va ella misma. A lo más 8: ALTOS DE MIRANDA → AM, SUBESTACION CFE CABO BELLO → SCCB, LA CALMA → CALMA, TORRE 2 → T2.
 */
export function siglasDeNombre(nombre) {
  const palabras = plegar(String(nombre ?? '')).toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
  const llenas = palabras.filter((w) => !PALABRAS_VACIAS.has(w));
  const usadas = llenas.length ? llenas : palabras;
  if (!usadas.length) return '';
  if (usadas.length === 1) return usadas[0].slice(0, LARGO_SIGLAS);
  return usadas.map((w) => (/^[0-9]/.test(w) ? w : w[0])).join('').slice(0, LARGO_SIGLAS);
}
